import { createBrowserHistory, History, Location } from 'history';
import * as React from 'react';
import { Redirect } from 'react-router';
import { RouteConfig } from 'react-router-config';

import AccountAppComponent from '@app/components/account/app/account-app';
import AccountAppViewComponent from '@app/components/account/app/account-app-view.component';
import ProfileComponent from '@app/components/account/profile/profile';
import PurchasePostageComponent from '@app/components/account/purchase-postage/purchase-postage';
import UserComponent from '@app/components/account/user/user';
import AccountLevelOverRides from '@app/components/admin/carriers/account-level-overrides.component';
import AdminCarrierDetailsComponent from '@app/components/admin/carriers/admin-carrier-details.component';
import CarriersComponent from '@app/components/admin/carriers/carriers';
import AdminAccountAppDetailsComponent from '@app/components/admin/customer/admin-account-app-details.component';
import AdminAccountDetailsComponent from '@app/components/admin/customer/admin-account-details.component';
import AdminCustomerComponent from '@app/components/admin/customer/admin-customer';
import AdminDashboardComponent from '@app/components/admin/dashboard/admin-dashboard';
import SettingComponent from '@app/components/admin/setting/settings';
import AdminShipmentComponent from '@app/components/admin/shipment/admin-shipment';
import ShipmentManifestComponent from '@app/components/admin/manifest/shipment-manifest';
import AdminUsersComponet from '@app/components/admin/users/admin-users';
import SideBarCarrierViewComponent from '@app/components/carriers/sidebar-carrier-view.component';
import CustomerComponent from '@app/components/customer/customer/customer';
import DashboardComponent from '@app/components/dashboard/dashboard/dashboard';
import ReportAccountStatementByDayComponent from '@app/components/report/account-statement-by-day/account-statement-by-day';
import ReportBalanceHistoryComponent from '@app/components/report/balance-history/balance-history';
import ReportDailyFinanceComponent from '@app/components/report/daily-finance/daily-finance';
import ReportMonthlyFinanceComponent from '@app/components/report/monthly-finance/monthly-finance';
import ShipmentComponent from '@app/components/shipment/shipment/shipment';
import { AccountStatusEnum, UserRole } from './app.enum';
import { IAccount, Module } from './models';

export interface IAppRouteConfig extends RouteConfig {
	key?: string;
	render?: () => any;
	accountStatuses?: AccountStatusEnum[];
	userRoles?: UserRole[];
}

interface IBreadCrumb {
	title: string;
	location: Location<IHistoryState>;
}

export interface IHistoryState {
	account?: Partial<IAccount>;
	breadcrumb?: IBreadCrumb | IBreadCrumb[];
	app?: any;
	extraParams?: IExtraParamsRouting;
	accessToken?: string;
	carrierId?: string;
	refreshToken?: string;
	tab?: 0 | 1 | 2 | 3;
}

export interface IExtraParamsRouting {
	isRoutedFrom: string;
}

export const AppRoutes: IAppRouteConfig[] = [
	{
		component: DashboardComponent,
		path: Module.Dashboard.url,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.AccountAdmin, UserRole.User],
	},
	{
		component: CustomerComponent,
		path: Module.Customers.url,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.AccountAdmin, UserRole.User],
		exact: true,
	},
	{
		component: ShipmentComponent,
		path: Module.Shipments.url,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.AccountAdmin, UserRole.User],
	},
	{
		component: AdminShipmentComponent,
		path: Module.AdminShipments.url,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.Admin, UserRole.ReadOnlyAdmin],
	},
	{
		component: ShipmentManifestComponent,
		path: Module.ShipmentManifestComponent.url,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.Admin, UserRole.ReadOnlyAdmin],
	},
	{
		component: AccountAppComponent,
		path: `${Module.Apps.url}/app-detail`,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.AccountAdmin, UserRole.User],
	},
	{
		component: AccountAppViewComponent,
		path: Module.Apps.url,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.AccountAdmin, UserRole.User],
	},
	{
		component: ReportBalanceHistoryComponent,
		path: Module.Reports.children.ReportAccountStatement.url,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.AccountAdmin, UserRole.User],
	},
	{
		component: ReportAccountStatementByDayComponent,
		path: Module.Reports.children.ReportAccountStatementByDay.url,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.AccountAdmin, UserRole.User],
	},
	{
		component: ReportDailyFinanceComponent,
		path: Module.Reports.children.ReportDailyFinance.url,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.AccountAdmin, UserRole.User],
	},
	{
		component: ReportMonthlyFinanceComponent,
		path: Module.Reports.children.ReportMonthlyFinance.url,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.AccountAdmin, UserRole.User],
	},
	{
		component: PurchasePostageComponent,
		path: Module.Billing.url,
		accountStatuses: [AccountStatusEnum.Active],
		userRoles: [UserRole.AccountAdmin, UserRole.User],
	},
	{
		component: UserComponent,
		path: Module.User.url,
		accountStatuses: [AccountStatusEnum.Active],
		userRoles: [UserRole.AccountAdmin, UserRole.User, UserRole.Admin, UserRole.ReadOnlyAdmin],
	},
	{
		component: ProfileComponent,
		path: Module.Profile.url,
		accountStatuses: [AccountStatusEnum.Active],
		userRoles: [UserRole.AccountAdmin, UserRole.User, UserRole.Admin, UserRole.ReadOnlyAdmin],
	},

	//////////////////////////////////////////
	{
		component: AdminDashboardComponent,
		path: Module.Dashboard.url,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.Admin, UserRole.ReadOnlyAdmin],
	},
	{
		component: AdminAccountAppDetailsComponent,
		path: `${Module.Accounts.url}/app/:appId`,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.Admin, UserRole.ReadOnlyAdmin],
		exact: true,
	},
	{
		component: AdminCustomerComponent,
		path: `${Module.Accounts.url}/:levelType`,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.Admin, UserRole.AccountAdmin, UserRole.ReadOnlyAdmin],
		exact: true,
	},
	{
		component: AdminAccountDetailsComponent,
		path: `${Module.Accounts.url}/:levelType/:accountId`,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.Admin, UserRole.ReadOnlyAdmin],
		exact: true,
	},
	{
		render: () => <Redirect path={Module.Accounts.url} to={Module.Accounts.redirect} exact={true} />,
		path: Module.Accounts.url,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.Admin, UserRole.AccountAdmin, UserRole.ReadOnlyAdmin],
		exact: true,
	},
	{
		component: AdminAccountDetailsComponent,
		path: `${Module.Customers.url}/:accountId`,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.AccountAdmin],
		exact: true,
	},
	{
		component: AdminAccountAppDetailsComponent,
		path: `${Module.Customers.url}/app/:appId`,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.AccountAdmin],
		exact: true,
	},
	{
		component: AdminUsersComponet,
		path: Module.User.url,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.Admin, UserRole.ReadOnlyAdmin],
	},
	{
		component: AccountLevelOverRides,
		path: '/carrier/:serviceType',
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.Admin, UserRole.ReadOnlyAdmin],
	},
	{
		component: AdminCarrierDetailsComponent,
		path: '/carrier',
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.Admin, UserRole.ReadOnlyAdmin],
	},
	{
		component: CarriersComponent,
		path: Module.Carriers.url,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.Admin, UserRole.ReadOnlyAdmin],
	},
	{
		component: SideBarCarrierViewComponent,
		path: Module.CarrierAccounts.url,
		exact: true,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.AccountAdmin, UserRole.User],
	},
	{
		component: SettingComponent,
		path: Module.Settings.url,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.Admin, UserRole.ReadOnlyAdmin],
	},

	//////////////////////////////////////////
	// If no route matches, redirect to dashboard
	{
		render: () => <Redirect to={Module.Dashboard.url} />,
		accountStatuses: [AccountStatusEnum.Active, AccountStatusEnum.Closed],
		userRoles: [UserRole.AccountAdmin, UserRole.User, UserRole.Admin, UserRole.ReadOnlyAdmin],
	},
];

export const history = createBrowserHistory({
	basename: '/',
}) as History<IHistoryState>;



// WEBPACK FOOTER //
// ./src/app.routes.tsx